import { createTheme, Theme } from '@mui/material/styles';
import { ColorPalette } from '../api/types';
import { DEFAULT_COLOR_PALETTE } from '../contexts/ColorContext';

// Create a theme factory that accepts dynamic colors
export const createDynamicTheme = (colors: ColorPalette = DEFAULT_COLOR_PALETTE): Theme => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary,
        light: lightenColor(colors.primary, 20),
        dark: darkenColor(colors.primary, 15),
        50: lightenColor(colors.primary, 45),
        100: lightenColor(colors.primary, 40),
        200: colors.border || lightenColor(colors.primary, 30),
        300: lightenColor(colors.primary, 25),
        400: lightenColor(colors.primary, 20),
        500: colors.primary,
        600: darkenColor(colors.primary, 10),
        700: colors.secondary,
        800: colors.foregroundColor,
      },
      secondary: {
        main: colors.secondary,
        light: lightenColor(colors.secondary, 15),
        dark: darkenColor(colors.secondary, 15),
      },
      background: {
        default: colors.background,
        paper: lightenColor(colors.primary, 40),
      },
      text: {
        primary: colors.foregroundColor,
        secondary: colors.muted || darkenColor(colors.primary, 10),
      },
      error: {
        main: colors.destructive || DEFAULT_COLOR_PALETTE.destructive!,
      },
      success: {
        main: colors.success || colors.primary,
      },
      warning: {
        main: colors.warning || DEFAULT_COLOR_PALETTE.warning!,
      },
    },
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      // @ts-ignore
      fontWeight: 400,
      fontStyle: 'normal',
      lineHeight: '22px',
      letterSpacing: '0.28px',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        color: colors.foregroundColor,
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      body1: {
        fontSize: '14px',
        lineHeight: '22px',
        color: colors.muted || darkenColor(colors.primary, 10),
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: '0.28px',
      },
      body2: {
        fontSize: '14px',
        lineHeight: '22px',
        color: colors.muted || darkenColor(colors.primary, 10),
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: '0.28px',
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'Montserrat',
        lineHeight: '22px',
        letterSpacing: '0.28px',
      },
      caption: {
        fontSize: '14px',
        lineHeight: '22px',
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: '0.28px',
      },
      overline: {
        fontSize: '14px',
        lineHeight: '22px',
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: '0.28px',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Montserrat',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
          contained: {
            boxShadow: `0 2px 8px ${hexToRgba(colors.primary, 0.3)}`,
            '&:hover': {
              boxShadow: `0 4px 12px ${hexToRgba(colors.primary, 0.4)}`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: `0 4px 20px ${hexToRgba(colors.primary, 0.08)}`,
            border: `1px solid ${hexToRgba(colors.primary, 0.05)}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
            '& .MuiInputBase-input': {
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '22px',
              letterSpacing: '0.28px',
            },
            '& .MuiInputBase-input::placeholder': {
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '22px',
              letterSpacing: '0.28px',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'normal',
            lineHeight: '22px',
            letterSpacing: '0.28px',
          },
        },
      },
    },
  });
};

// Default theme using default colors
export const theme = createDynamicTheme();

// Utility functions
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, ((num >> 16) & 0xff) + amt);
  const G = Math.min(255, ((num >> 8) & 0xff) + amt);
  const B = Math.min(255, (num & 0xff) + amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, ((num >> 16) & 0xff) - amt);
  const G = Math.max(0, ((num >> 8) & 0xff) - amt);
  const B = Math.max(0, (num & 0xff) - amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
 